import { api } from './client';
import { i18nMetaMap } from '@/lib/i18n-map';
import '@/lib/i18n-map';

export interface ChatChannel {
  id: string;
  key: string;
  label: string;
  icon: string | null;
  color: string | null;
  descr: string | null;
  roles: string[];
  members: string[];
  sortOrder: number;
  unread: number;
  /** Among the unread, at least one marked „urgent". */
  urgentUnread: boolean;
}

export interface ChatMessage {
  id: string;
  channel: string | null;
  fromStaffId: string | null;
  fromName: string | null;
  toStaffId: string | null;
  text: string;
  urgent: boolean;
  replyTo: string | null;
  replyText: string | null;
  replyFrom: string | null;
  patientRef: string | null;
  patientName: string | null;
  attachmentPath: string | null;
  attachmentType: string | null;
  attachmentName: string | null;
  edited: boolean;
  editedAt: string | null;
  deleted: boolean;
  pinned: boolean;
  pinnedBy: string | null;
  createdAt: string;
  reactions: Array<{ emoji: string; count: number; mine: boolean }>;
}

/** Un marcaj de citire, așa cum îl întoarce serverul. */
export interface ChatReader {
  staffId: string;
  name: string;
  lastReadAt: string;
}

export interface DirectConversation {
  staffId: string;
  name: string;
  chatStatus: ChatStatus;
  lastMessageAt: string | null;
  lastText: string | null;
  lastFromMe: boolean;
  unread: number;
  /** Among the unread, at least one marked „urgent". */
  urgentUnread: boolean;
}

/** Where a colleague is, in one word — the four the original offered. */
export type ChatStatus = 'online' | 'busy' | 'break' | 'away';
export const CHAT_STATUS = i18nMetaMap<ChatStatus, { label: string; tone: string }>('common', {
  online: { label: 'disponibil', tone: 'ok' },
  busy: { label: 'inConsultatie', tone: 'bad' },
  break: { label: 'pauza', tone: 'warn' },
  away: { label: 'plecat', tone: 'muted' },
});

/** Written out rather than derived: the API rejects unknown properties. */
export interface SendMessageInput {
  channel?: string;
  toStaffId?: string;
  text: string;
  urgent?: boolean;
  replyTo?: string | null;
  patientRef?: string | null;
  attachmentPath?: string | null;
  attachmentType?: string | null;
  attachmentName?: string | null;
}

export interface CreateChannelInput {
  key: string;
  label: string;
  icon?: string | null;
  color?: string | null;
  descr?: string | null;
  roles?: string[];
  members?: string[];
  sortOrder?: number;
}

/** Written out rather than derived: the key of a channel never changes. */
export interface UpdateChannelInput {
  label?: string;
  icon?: string | null;
  color?: string | null;
  descr?: string | null;
  /** Empty: every role. */
  roles?: string[];
  /** Empty: everyone; otherwise these colleagues only. */
  members?: string[];
  sortOrder?: number;
}

export const chatApi = {
  channels: () => api.get<ChatChannel[]>('/chat/channels'),
  createChannel: (body: CreateChannelInput) => api.post<ChatChannel>('/chat/channels', body),
  /** Redenumește canalul, sau schimbă cine e în el — doar un moderator. */
  updateChannel: (id: string, body: UpdateChannelInput) =>
    api.patch<ChatChannel>(`/chat/channels/${id}`, body),
  removeChannel: (id: string) => api.delete<void>(`/chat/channels/${id}`),

  direct: () => api.get<DirectConversation[]>('/chat/direct'),

  /** Totalul de necitite — insigna din bara laterală. */
  unread: () => api.get<{ total: number }>('/chat/unread'),
  setStatus: (status: ChatStatus) => api.patch<{ status: ChatStatus }>('/chat/status', { status }),

  messages: (query: {
    channel?: string;
    withStaffId?: string;
    since?: string;
    /** „Mesaje mai vechi”: pagina de dinaintea celui mai vechi mesaj încărcat. */
    before?: string;
    limit?: number;
  }) => api.get<ChatMessage[]>('/chat/messages', { query }),
  send: (body: SendMessageInput) => api.post<ChatMessage>('/chat/messages', body),
  edit: (id: string, text: string) => api.patch<ChatMessage>(`/chat/messages/${id}`, { text }),
  remove: (id: string) => api.delete<ChatMessage>(`/chat/messages/${id}`),
  pin: (id: string, pinned: boolean) =>
    api.post<ChatMessage>(`/chat/messages/${id}/pin`, { pinned }),
  react: (id: string, emoji: string) =>
    api.post<ChatMessage>(`/chat/messages/${id}/react`, { emoji }),
  /** Încarcă un fișier; mesajul care îl poartă se trimite separat. */
  uploadAttachment: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<{ path: string; type: string; name: string }>('/chat/attachments', form);
  },

  /** Link scurt către un atașament — serverul verifică întâi că ai voie. */
  attachmentUrl: (path: string) =>
    api.get<{ url: string }>('/chat/attachments/url', { query: { path } }),

  /** Cine a citit conversația, și până când — pentru „✓ Văzut de". */
  readers: (scope: string) => api.get<ChatReader[]>('/chat/reads', { query: { scope } }),

  markRead: (scope: string) => api.post<void>('/chat/read', { scope }),
};
